/**
 * Topology API client.
 *
 * Mirrors `backend/src/hub/routes/topology.py`. Returns the live
 * data-flow graph derived from active workflows + ingress sources +
 * canonical schemas. Used by the Topology page (ReactFlow).
 */
import { api } from "./api";

export type TopologyNodeType = "ingress" | "canonical" | "external_sink";

export interface TopologyNode {
  id: string;
  type: TopologyNodeType;
  label: string;
  sublabel: string | null;
  /** Underlying entity id (UUID for ingress, "type/vN" for canonical). */
  ref_id: string | null;
  tags: string[];
  enabled: boolean;
  meta: Record<string, unknown>;
}

/** Run-time signal aggregated over the trailing window (default 24h). */
export interface WorkflowMetrics {
  runs_total: number;
  runs_succeeded: number;
  runs_failed: number;
  runs_running: number;
  /** Most-recent terminal state, or ``running`` / ``null`` if no runs. */
  last_status: "succeeded" | "failed" | "running" | "cancelled" | null;
  last_finished_at: string | null;
}

export interface TopologyEdge {
  id: string;
  source: string;
  target: string;
  workflow_id: string;
  workflow_name: string;
  direction: "inbound" | "internal" | "outbound";
  enabled: boolean;
  tags: string[];
  metrics: WorkflowMetrics;
}

export interface TopologyResponse {
  nodes: TopologyNode[];
  edges: TopologyEdge[];
  /** Trailing window the metrics cover, in hours. */
  window_hours: number;
}

export const topologyApi = {
  /** Repeatable ``tag`` query param applies AND semantics. */
  get: (params?: { tag?: string[] }) =>
    api.get<TopologyResponse>("/topology", { query: params }),
};
